import { Component, h, Prop, Host, Fragment, State } from '@stencil/core';
import {
  ActionTrackingEvent,
  VirtualPageTrackingEvent,
} from '@union-investment/tracking/src/events';
import { fetchPropertyData } from '../fetch-realestate-data';
import store, { FilterOption } from '../store';

@Component({
  tag: 'ui-realestate-search-filters',
  styleUrl: 'ui-realestate-search-filters.scss',
  shadow: false,
})
export class UiRealEstateFilters {
  @Prop() usageTypeId: string;

  @Prop() resultsPerPage: number;

  @Prop() apiUrl: string;

  @Prop() apiKey: string;

  @Prop() apiVersion: string;

  yearMin: HTMLUiInputElement;

  yearMax: HTMLUiInputElement;

  @State() error = false;

  offcanvasId = 'realestate-filters';

  componentWillLoad() {
    // update selectedFilters with query params if they exist
    if (Object.keys(this.getFiltersFromUrl()).length > 0) {
      store.state.selectedFilters = this.getFiltersFromUrl();
      this.updateFiltersHandler();
    }
  }

  private async fetchFiltersHandler() {
    const fetchedFilters = await fetchPropertyData({
      actionType: 'filters',
      apiUrl: this.apiUrl,
      apiKey: this.apiKey,
      apiVersion: this.apiVersion,
    });
    return fetchedFilters.filterGroups;
  }

  async updateFiltersHandler() {
    // update URL with query params
    this.updateUrlWithQueryParams();
    const fetchedFilters = await this.fetchFiltersHandler();
    store.state.filterGroups = [...fetchedFilters];
    const usageTypeIsSetViaProp = this.usageTypeId;
    if (usageTypeIsSetViaProp) {
      store.state.filterGroups = store.state.filterGroups.filter(
        (el) => el.name !== 'usage'
      );
    }
  }

  private getFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);

    // return object with query params
    const filters = {};
    params.forEach((value, key) => {
      filters[key] = value;
    });

    return filters;
  };

  private setUrlQueryParams = (): string => {
    const url = new URL(window.location.href);

    const params = new URLSearchParams(store.state.selectedFilters);

    url.search = params.toString();

    return url.toString();
  };

  private updateUrlWithQueryParams = () => {
    window.history.pushState({}, '', this.setUrlQueryParams());
  };

  /**
   * Transforms filter options array to a choices array
   * in order to properly generate choices in ui-select
   */
  choicesArrayTransform(filter) {
    let updatedChoicesArray = [];
    const currentFilter = store.state.filterGroups.find(
      (el) => el.name === filter.name
    );

    // push default option first
    updatedChoicesArray.push({
      value: '',
      label: currentFilter.default,
      selected: this.isSelectedHandler({
        title: currentFilter.name,
        value: '',
      }),
    });
    // add other filter options to array
    const filterOptions = currentFilter.filter.map((option) => ({
      value: option.value,
      label: option.title,
      selected: this.isSelectedHandler({
        title: currentFilter.name,
        value: option.value,
      }),
    }));
    updatedChoicesArray = [...updatedChoicesArray, ...filterOptions];
    return updatedChoicesArray;
  }

  filterChangeHandler(filter: FilterOption) {
    /**
     * Remove the filter property from store.state.selectedFilters
     *  when the filter value is empty
     */
    if (filter.value) {
      store.state.selectedFilters[filter.title] = filter.value;
    } else {
      delete store.state.selectedFilters[filter.title];
    }
    this.updateFiltersHandler();
  }

  /** Validate year input. Returns true if value given is a 4-digit number.
   * Also returns true if value is an empty string,
   * which allows the user to clear the input field without triggering a validation error.
   */
  validateYearInput(value: string) {
    const fourDigitYearRegex = /\d{4}/;
    return value === '' || fourDigitYearRegex.test(value);
  }

  onChangeHandler(event: Event, type: string) {
    let filterValue;
    let filterName;

    // check if input or select and get values
    if (type === 'select') {
      filterValue = (event.target as HTMLUiSelectElement).querySelector(
        'option'
      ).value;
      filterName = (event.target as HTMLUiSelectElement).name;
    } else {
      filterValue = (event.target as HTMLUiInputElement).value;
      filterName = (event.target as HTMLUiInputElement).name;
      // check if year is valid and update filterValue
      if (!this.validateYearInput(filterValue)) {
        this.error = true;
        // set value to empty string to avoid saving invalid value to selectedFilters
        filterValue = '';
      } else {
        this.error = false;
      }
    }
    this.filterChangeHandler({ title: filterName, value: filterValue });
  }

  /**
   * Set selected attribute on the filters based on
   * which filter option was selected
   */

  isSelectedHandler(filter: FilterOption) {
    let isSelected = false;
    const selectedFilters = { ...store.state.selectedFilters };

    // set default values as selected on first render
    if (Object.keys(selectedFilters).length === 0 && filter.value === '') {
      isSelected = true;
    }
    if (Object.keys(selectedFilters).length > 0) {
      // check if filter name exists in selectedFilters and set selected for the matching option.
      // eslint-disable-next-line no-prototype-builtins
      if (selectedFilters.hasOwnProperty(filter.title)) {
        isSelected = selectedFilters[filter.title] === filter.value;
      } else {
        isSelected = filter.value === '';
      }
    }
    return isSelected;
  }

  private async searchHandler() {
    const searchResults = await fetchPropertyData({
      actionType: 'search',
      apiUrl: this.apiUrl,
      apiKey: this.apiKey,
      apiVersion: this.apiVersion,
    });
    store.state.searchResults = { ...searchResults.results };

    this.track();
  }

  /**
   * Tracks user interactions and virtual page views related to real estate search.
   * @private
   * @returns {void}
   */
  private track(): void {
    const eventName = 'Real Estate Search';
    const customECommerceParameter = this.getCustomECommerceParameter();

    const actionTrackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName,
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: store.state.i18n.searchProperties || 'Suchen',
        virtualTarget: 'app_Immobiliensuche',
      },
      category: {
        area: 'Content',
      },
    };

    const virtualPageTrackingEvent: VirtualPageTrackingEvent = {
      eventInfo: {
        eventName,
        eventType: 'VirtualPage',
      },
      attributes: {
        virtualContentGroups: ['app_Immobiliensuche'],
        customParameter: {
          1: store.state.searchResults.currentResults,
        },
        customECommerceParameter: {
          2: 'Immobiliensuche',
          31: customECommerceParameter,
        },
      },
    };

    window.ui?.services?.tracking?.track(actionTrackingEvent);
    window.ui?.services?.tracking?.track(virtualPageTrackingEvent);
  }

  /**
   * Retrieves custom eCommerce parameters based on selected filters and default values.
   * @private
   * @returns {string} A string representing concatenated filter values.
   */
  private getCustomECommerceParameter() {
    const eCommerceParameter = {};

    // add default value for each filter input to eCommerceParameter as initial values
    store.state.filterGroups.forEach((item) => {
      eCommerceParameter[item.name] = item.default;
    });

    // update eCommerceParameter with any selected filter values
    // the value will be a string like 'b4c97989-2a50-11e7-b6a7-005056874be5' used for the API
    Object.entries(store.state.selectedFilters).forEach(([key, value]) => {
      // get the corresponding filter group which contains the filter options for a given field
      const filterGroup = store.state.filterGroups.find(
        (item) => item.name === key
      );
      // find the filter option that matches the selected value and get the readable title
      const matchingFilter = filterGroup?.filter.find(
        (item) => item.value === value
      );

      if (matchingFilter.title) {
        eCommerceParameter[key] = matchingFilter.title;
      }
    });

    return Object.values(eCommerceParameter).join('.').replace(/\s+/g, '_');
  }

  resetFiltersHandler() {
    // reset filters
    const usageTypeIsSetViaProp = this.usageTypeId;
    if (usageTypeIsSetViaProp) {
      // remove all filters except usage
      const selectedFilters = { ...store.state.selectedFilters };
      Object.keys(selectedFilters).forEach((key) => {
        if (selectedFilters[key] !== this.usageTypeId) {
          delete selectedFilters[key];
        }
      });
      store.state.selectedFilters = selectedFilters;
    } else {
      store.state.selectedFilters = {};
    }

    // reset min/max year values
    this.yearMin.value = '';
    this.yearMax.value = '';

    this.updateFiltersHandler();
    this.searchHandler();
  }

  /**
   * Renders search result info and reset button
   */
  renderSearchInfo() {
    let checkIfFilterIsSelected: boolean;
    const selectedFilterKeysArr = Object.keys(store.state.selectedFilters);
    // usage is already selected as a filter if usageTypeId exists
    if (this.usageTypeId) {
      checkIfFilterIsSelected = selectedFilterKeysArr.length > 1;
    } else {
      checkIfFilterIsSelected = selectedFilterKeysArr.length > 0;
    }
    const resetFilters = (
      <ui-offcanvas-trigger offcanvas-id={this.offcanvasId}>
        <ui-button
          variant={store.state.isMobile ? 'secondary' : 'plain'}
          onClick={() => this.resetFiltersHandler()}
        >
          {store.state?.i18n?.resetFilters || 'Alle Filter Zurücksetzen'}
        </ui-button>
      </ui-offcanvas-trigger>
    );
    let resultsInfo;
    const { currentResults, totalResults } = store.state.searchResults;
    if (Number(currentResults) < Number(totalResults)) {
      resultsInfo = `${currentResults} ${store.state.i18n.from || 'von'} ${totalResults}`;
    } else {
      resultsInfo = totalResults;
    }
    // Add fallback if currentResults is undefined
    // This happens because when there are no results found
    // the currentResults property doesn't exist on the api response instead of being equal to 0
    if (!currentResults) {
      resultsInfo = 0;
    }

    return (
      <div class="search-info">
        <span>
          {resultsInfo} {store.state.i18n.realEstateObjects || 'Objekte'}
        </span>
        {checkIfFilterIsSelected && resetFilters}
      </div>
    );
  }

  render() {
    return (
      <Host>
        {store.state.filterGroups.length ? (
          <Fragment>
            <div class="filters">
              {store.state.filterGroups.map((filter, i) => (
                <div class="filter-item" key={i}>
                  <ui-select
                    name={filter.name}
                    onChange={(event: Event) =>
                      this.onChangeHandler(event, 'select')
                    }
                    choicesArray={this.choicesArrayTransform(filter)}
                  ></ui-select>
                </div>
              ))}
              <div class="filter-item years">
                <div class="years__label">
                  {store.state.i18n.acquisitionYear || 'Erwerbsjahr'}
                </div>
                <div class="years__inputs">
                  <ui-input
                    name="yearMin"
                    type="number"
                    ref={(el: HTMLUiInputElement) => {
                      this.yearMin = el;
                    }}
                    placeholder={store.state.i18n.from || 'von'}
                    onChange={(event: Event) =>
                      this.onChangeHandler(event, 'input')
                    }
                    error={this.error}
                    value={store.state.selectedFilters?.yearMin || ''}
                  ></ui-input>
                  <ui-input
                    name="yearMax"
                    type="number"
                    ref={(el: HTMLUiInputElement) => {
                      this.yearMax = el;
                    }}
                    placeholder={store.state.i18n.to || 'bis'}
                    onChange={(event: Event) =>
                      this.onChangeHandler(event, 'input')
                    }
                    error={this.error}
                    value={store.state.selectedFilters?.yearMax || ''}
                  ></ui-input>
                </div>
                {this.error && (
                  <p class="years__error-message">
                    {store.state.i18n.yearError ||
                      'Das Jahr muss mit 4 Ziffern angegeben werden.'}
                  </p>
                )}
              </div>
            </div>
            <ui-offcanvas-trigger offcanvas-id={this.offcanvasId}>
              <ui-button variant="primary" onClick={() => this.searchHandler()}>
                {store.state.i18n.searchProperties || 'Suchen'}
              </ui-button>
            </ui-offcanvas-trigger>
            {this.renderSearchInfo()}
          </Fragment>
        ) : (
          <sd-spinner style={{ fontSize: '2rem' }} color="primary"></sd-spinner>
        )}
      </Host>
    );
  }
}
